import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import MediatorManager from "../../../core/application/MediatorManager";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import MapCharacterInteractionEvent from "../event/MapCharacterInteractionEvent";
import OpenMapCharacterMenuEvent from "../event/OpenMapCharacterMenuEvent";
import MapInteractionEvent from "../event/MapInteractionEvent";
import MapLocationDefinition from "../model/MapLocationDefinition";
import MapLocationButton from "./MapLocationButton";
import CloseMapCharacterMenuEvent from "../event/CloseMapCharacterMenuEvent";

export default class MapLocationMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {MediatorManager} mediatorManager 
     * @param {AssetLibrary} assetLibrary 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, mediatorManager, assetLibrary) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._mediatorManager = mediatorManager;
        this._assetLibrary = assetLibrary;

        /** @type {MapLocationDefinition} */
        this._location = this.getCustomData().location;

        this._handleButtonBound = this._handleButton.bind(this);
    }

    initialize() {
        super.initialize();

        this._createCharacterMenu();

        this.registerEventListener(CloseMapCharacterMenuEvent.CLOSE_ALL, this._handleCloseMenu.bind(this));
        this.registerEventListener(OpenMapCharacterMenuEvent.OPEN_MENU_FOR_LOCATION, this._handleOpenMenuForLocation.bind(this));

        this._button = new MapLocationButton(this._target);
        this._button.setLabel(this._languagePack.getText(`map.locations.${this._location.id}.name`));
        this._button._noCharacterAvailable();

        const availableLocationImage = this._assetLibrary.getAsset(this._location.imageId);
        const availableLocationBitmap = new createjs.Bitmap(availableLocationImage);

        const unavailableLocationImage = this._assetLibrary.getAsset(`${this._location.imageId}_disabled`);
        const unavailableLocationBitmap = new createjs.Bitmap(unavailableLocationImage);

        const locationContainer = this._target.locationImage.locationContainer;

        locationContainer.removeAllChildren();
        locationContainer.addChild(availableLocationBitmap);
        locationContainer.addChild(unavailableLocationBitmap);

        this._availableLocationBitmap = availableLocationBitmap;
        this._unavailableLocationBitmap = unavailableLocationBitmap;

        this._updateLocationBitmap();
        this._button.addEventListener('click', this._handleButtonBound);
        this.disable();
    }

    destroy() {
        super.destroy();
        this._characterMenuMediator.destroy();
        this._button.removeEventListener('click', this._handleButtonBound);
        this._button.destroy();
    }

    hideInstant() {
        this._target.alpha = 0;
        this._target.scale = 0;
    }

    hide() {
        return new Promise(resolve => {
            createjs.Tween.get(this._target)
                .to({ alpha: 0, scale: 0 }, 450, createjs.Ease.backIn)
                .call(resolve);
        });
    }

    show() {
        return new Promise(resolve => {
            createjs.Tween.get(this._target)
                .to({ alpha: 1, scale: 1 }, 450, createjs.Ease.backOut)
                .call(resolve);
        });
    }

    disable() {
        this._button.setEnabled(false);
        this._updateLocationBitmap();
        this._button._noCharacterAvailable();
    }

    enable() {
        this._button.setEnabled(true);
        this._updateLocationBitmap();
        this._button._newCharacterAvailable();
    }

    _handleButton(evt) {
        const openMapCharacterMenuEvent = new OpenMapCharacterMenuEvent(OpenMapCharacterMenuEvent.OPEN_MENU_FOR_LOCATION, this._location);
        this.dispatch(openMapCharacterMenuEvent.type, { data: openMapCharacterMenuEvent });
    }

    _handleOpenMenuForLocation(evt) {
        const data = evt.params.data;
        const location = data?.location;
        if (this._location.id === location.id) {
            this._showCharacterMenu();
        } else {
            this._hideCharacterMenu();
        }
    }

    _handleCloseMenu() {
        this._hideCharacterMenu();
    }

    _showCharacterMenu() {
        this._button.setSelected(true);
        this._characterMenuMediator.show();
    }

    _hideCharacterMenu() {
        this._button.setSelected(false);
        this._characterMenuMediator.hide();
    }

    _createCharacterMenu() {
        const characterMenuSkin = new lib.MainAssets.MapLocationCharacterMenu();
        characterMenuSkin.customData = {
            location: this._location,
        };

        this._target.parent.addChild(characterMenuSkin);
        characterMenuSkin.x = this._target.x;
        characterMenuSkin.y = this._target.y;


        this._characterMenuMediator = this._mediatorManager.createMediator(characterMenuSkin, SkinKeyRegistry.MAP_LOCATION_CHARACTER_MENU);
    }

    _updateLocationBitmap() {
        this._availableLocationBitmap.visible = !!this.enabled;
        this._unavailableLocationBitmap.visible = !this.enabled;
    }

    get location() {
        return this._location;
    }

    get enabled() {
        return this._button.getEnabled();
    }
}

MapLocationMediator.CLICK = "click";